import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Upload as UploadIcon
} from "@mui/icons-material";
import {Box, Button, Card, CardContent, Chip, Typography} from "@mui/material";
import {useState} from "react";

const SchemaManager = ({isSubmitting, onSchemaContentChange, onError, onSaveMessage}) => {
  const [schemaFile, setSchemaFile] = useState(null);
  const [schemaContent, setSchemaContent] = useState(null);
  const [schemaText, setSchemaText] = useState("");

  const handleSchemaChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSchemaFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonString = e.target?.result;
          const parsedJson = JSON.parse(jsonString);
          setSchemaContent(parsedJson);
          setSchemaText(JSON.stringify(parsedJson, null, 2));
          onSchemaContentChange(parsedJson);
          onError("");
          console.log("✅ Schema JSON cargado correctamente:", parsedJson);
        } catch (err) {
          onError("❌ Error: El archivo no es un JSON válido");
          console.error(err);
          setSchemaFile(null);
          setSchemaContent(null);
          setSchemaText("");
        }
      };
      reader.readAsText(file);
    }
  };

  const handleSaveToLocalStorage = () => {
    if (!schemaContent) {
      onError("⚠️ Por favor selecciona un archivo JSON schema primero");
      return;
    }

    try {
      const data = {
        schema: schemaContent,
        schemaName: schemaFile?.name,
        timestamp: new Date().toISOString()
      };

      localStorage.setItem("invoiceSchema", JSON.stringify(data));
      onSaveMessage("✅ Schema guardado correctamente en localStorage");
      onError("");

      console.log("Schema guardado exitoso:", {
        schemaName: schemaFile?.name,
        schemaKeys: Object.keys(schemaContent),
        timestamp: data.timestamp
      });
    } catch (err) {
      onError("❌ Error al guardar en localStorage: " + err.message);
      console.error(err);
    }
  };

  const handleClearLocalStorage = () => {
    if (window.confirm("¿Estás seguro de que quieres limpiar el schema guardado?")) {
      localStorage.removeItem("invoiceSchema");
      setSchemaFile(null);
      setSchemaContent(null);
      setSchemaText("");
      onSchemaContentChange(null);
      onSaveMessage("🗑️ Schema eliminado del localStorage");
      console.log("LocalStorage limpiado");
    }
  };

  const handleLoadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem("invoiceSchema");
      if (saved) {
        const data = JSON.parse(saved);
        setSchemaContent(data.schema);
        setSchemaText(JSON.stringify(data.schema, null, 2));
        onSchemaContentChange(data.schema);
        onSaveMessage(
          `✅ Schema cargado desde localStorage\n\nSchema: ${data.schemaName}\nFecha: ${new Date(data.timestamp).toLocaleString()}`
        );
      } else {
        onError("⚠️ No hay schema guardado en localStorage");
      }
    } catch (err) {
      onError("❌ Error al cargar desde localStorage: " + err.message);
      console.error(err);
    }
  };

  const handleExportSchema = () => {
    try {
      const saved = localStorage.getItem("invoiceSchema");
      if (!saved) {
        onError("⚠️ No hay schema guardado para exportar");
        return;
      }

      const data = JSON.parse(saved);
      const schemaToExport = data.schema;

      const fileName = prompt("Nombre del archivo (sin extensión):", data.schemaName?.replace(".json", "") || "schema");

      if (fileName === null) {
        return;
      }

      const finalFileName = fileName.trim() || "schema";

      const blob = new Blob([JSON.stringify(schemaToExport, null, 2)], {
        type: "application/json"
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${finalFileName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      onSaveMessage(`📥 Schema exportado como ${finalFileName}.json`);

      console.log("Schema exportado:", finalFileName);
    } catch (err) {
      onError("❌ Error al exportar schema: " + err.message);
      console.error(err);
    }
  };

  return (
    <Card variant="outlined" sx={{bgcolor: "success.50", border: 2, borderColor: "success.main", height: "100%"}}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{display: "flex", alignItems: "center", gap: 1}}>
          📋 Schema JSON
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Sube un archivo JSON con el schema y guárdalo en localStorage
        </Typography>
        <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 2}}>
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            disabled={isSubmitting}
            color="success"
          >
            Seleccionar JSON
            <input type="file" hidden accept=".json,application/json" onChange={handleSchemaChange} />
          </Button>
          {schemaFile && <Chip label={`✅ ${schemaFile.name}`} color="success" size="small" />}
        </Box>
        {schemaText && (
          <Box sx={{p: 2, bgcolor: "white", borderRadius: 1, border: 1, borderColor: "grey.300", mb: 2}}>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              Contenido del schema:
            </Typography>
            <pre
              style={{
                fontSize: "11px",
                margin: 0,
                whiteSpace: "pre-wrap",
                maxHeight: "200px",
                overflow: "auto"
              }}
            >
              {schemaText}
            </pre>
          </Box>
        )}

        <Box sx={{display: "flex", gap: 2, flexWrap: "wrap"}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveToLocalStorage}
            disabled={!schemaContent || isSubmitting}
            startIcon={<SaveIcon />}
          >
            Guardar
          </Button>

          <Button
            variant="outlined"
            color="success"
            onClick={handleExportSchema}
            disabled={isSubmitting}
            startIcon={<DownloadIcon />}
          >
            Exportar
          </Button>

          <Button
            variant="outlined"
            color="info"
            onClick={handleLoadFromLocalStorage}
            disabled={isSubmitting}
            startIcon={<UploadIcon />}
          >
            Cargar
          </Button>

          <Button
            variant="outlined"
            color="error"
            onClick={handleClearLocalStorage}
            disabled={isSubmitting}
            startIcon={<DeleteIcon />}
          >
            Limpiar
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SchemaManager;
